import { Component, EventEmitter, Inject, Optional, Output } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { MatDialog, MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { LimitModalComponent } from 'src/app/free-plan/limit-modal/limit-modal.component'
import { BuilderService } from 'src/app/builder-services/builder.service'
import { environment } from 'src/environments/environment'

export interface ScrapedPost {
  id: string
  imageUrl: string
  caption: string
  selected: boolean
}

export interface SelectedPost {
  imageUrl: string
  caption: string
}

export interface ScrapperDialogData {
  /** Max products the user may select. Undefined = unlimited. */
  maxSelection?: number;
}

export interface ScrapperDialogResult {
  action: 'import' | 'cancel'
  images?: SelectedPost[]
}

type Platform = 'instagram' | 'facebook' | 'whatsapp' | 'unknown'

@Component({
  selector: 'app-scrapper',
  templateUrl: './scrapper.component.html',
  styleUrl: './scrapper.component.scss'
})
export class ScrapperComponent {
  socialUrl: string = ''
  loading: boolean = false
  loadingMore: boolean = false
  error: string = ''
  posts: ScrapedPost[] = []
  selectedPosts: SelectedPost[] = []
  detectedPlatform: Platform = 'unknown'
  currentLimit: number = 25
  hasMoreImages: boolean = true

  get maxSelection(): number | undefined {
    return this.data?.maxSelection
  }

  get atLimit(): boolean {
    return this.maxSelection !== undefined && this.selectedPosts.length >= this.maxSelection
  }

  @Output() onClose = new EventEmitter<void>()
  @Output() onImport = new EventEmitter<SelectedPost[]>()

  // Shared WordPress scraper (scraper.php) — also used by stacks-chat's
  // SocialScraperService. The scrape runs server-side so the Apify token never
  // reaches the browser, and both frontends fix scraping bugs in one place.
  private readonly SCRAPE_URL = `${environment.builderURL}scraper/scrape`
  private readonly PROXY_URL = environment.proxyUrl

  constructor(
    private http: HttpClient,
    private dialog: MatDialog,
    private builderService: BuilderService,
    @Optional() public dialogRef: MatDialogRef<ScrapperComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: ScrapperDialogData
  ) {}

  detectPlatform(url: string): Platform {
    const lowerUrl = url.toLowerCase()
    if (lowerUrl.includes('instagram.com') || lowerUrl.includes('instagr.am')) {
      return 'instagram'
    }
    if (lowerUrl.includes('facebook.com') || lowerUrl.includes('fb.com') || lowerUrl.includes('fb.watch')) {
      return 'facebook'
    }
    if (lowerUrl.includes('whatsapp.com') || lowerUrl.includes('wa.me')) {
      return 'whatsapp'
    }
    return 'unknown'
  }

  scrapeImages(): void {
    if (!this.socialUrl) return

    this.detectedPlatform = this.detectPlatform(this.socialUrl)

    if (this.detectedPlatform === 'unknown') {
      this.error = 'Please enter a valid Instagram, Facebook, or WhatsApp URL'
      return
    }

    this.loading = true
    this.error = ''
    this.posts = []
    this.selectedPosts = []
    this.currentLimit = 25
    this.hasMoreImages = true

    if (this.detectedPlatform === 'whatsapp') {
      this.scrapeWhatsApp()
      return
    }

    this.scrapeSocial()
  }

  loadMore(): void {
    if (this.loadingMore || !this.hasMoreImages) return

    this.loadingMore = true
    const newLimit = this.currentLimit + 25

    this.scrapeSocial(true, newLimit)
  }

  // Delegates to the shared WordPress endpoint (POST wp-json/v4/scraper/scrape) — no
  // Apify token is shipped to the browser; it tries Meta Graph first and falls back
  // to Apify. Its posts carry no id, so imageUrl is used as the id below.
  //
  // The backend exposes no offset/cursor, so "load more" re-requests with a bigger
  // limit and re-fetches the whole set rather than appending a page. Results are not
  // guaranteed to be stable or ordered between calls, so selections are restored by
  // post id, never by array index.
  private scrapeSocial(isLoadMore: boolean = false, limit: number = this.currentLimit): void {
    const payload = { url: this.socialUrl, limit }

    this.http.post<{ posts: any[] }>(this.SCRAPE_URL, payload).subscribe({
      next: (response) => {
        this.loading = false
        this.loadingMore = false

        const rawPosts = response?.posts ?? []
        const newPosts: ScrapedPost[] = rawPosts
          .filter((item) => item.imageUrl)
          .map((item) => ({
            id: item.id || item.imageUrl,
            imageUrl: this.proxyImage(item.imageUrl),
            caption: item.caption || '',
            selected: false
          }))

        // A full page means there may be more; a short page means the source is exhausted.
        this.hasMoreImages = newPosts.length >= limit
        this.currentLimit = limit

        this.posts = this.mergePosts(isLoadMore, newPosts)

        if (this.posts.length === 0) {
          this.error = 'No images found for this profile'
        }
      },
      error: (err) => {
        this.loading = false
        this.loadingMore = false
        this.error = err.error?.message || 'Failed to scrape. Please check the URL and try again.'
        console.error('Scraping error:', err)
      }
    })
  }

  // Merges freshly scraped posts with the existing list, keeping already-selected
  // posts (and their selection state) when loading more instead of replacing them.
  private mergePosts(isLoadMore: boolean, newPosts: ScrapedPost[]): ScrapedPost[] {
    if (!isLoadMore) {
      return newPosts
    }

    const selectedById = new Map(
      this.posts.filter((post) => post.selected).map((post) => [post.id, post])
    )

    return newPosts.map((post) => selectedById.get(post.id) ?? post)
  }

  private scrapeWhatsApp(): void {
    // WhatsApp catalog scraping - similar implementation
    this.loading = false
    this.error = 'WhatsApp catalog scraping is coming soon'
  }

  trackByPostId(_index: number, post: ScrapedPost): string {
    return post.id
  }

  togglePostSelection(post: ScrapedPost): void {
    if (!post.selected && this.atLimit) {
      this.openUpgradeModal()
      return
    }
    post.selected = !post.selected
    this.updateSelectedPosts()
  }

  openUpgradeModal(): void {
    const ref = this.dialog.open(LimitModalComponent, {
      width: '337px',
      maxWidth: '90vw',
      panelClass: 'limit-dialog',
      backdropClass: 'blur-backdrop',
      hasBackdrop: true,
      disableClose: false,
      autoFocus: false,
      data: {
        title: 'Free Plan Limit Reached',
        message: `You can have up to 20 products on the free plan.`,
        cta: 'Upgrade Plan'
      }
    })
    ref.afterClosed().subscribe((upgrade) => {
      if (upgrade) {
        const projectId = this.builderService.selectedProject
        window.open(
          `${environment.stacksURL}pricing/${projectId ? `?project_id=${encodeURIComponent(projectId)}` : ''}`,
          '_blank'
        )
      }
    })
  }

  updateSelectedPosts(): void {
    this.selectedPosts = this.posts
      .filter((post) => post.selected)
      .map((post) => ({
        imageUrl: post.imageUrl,
        caption: post.caption,
      }))
  }

  selectAll(): void {
    if (this.maxSelection !== undefined) {
      let count = 0
      this.posts.forEach((post) => {
        post.selected = count < this.maxSelection! ? (count++, true) : false
      })
    } else {
      this.posts.forEach((post) => (post.selected = true))
    }
    this.updateSelectedPosts()
  }

  deselectAll(): void {
    this.posts.forEach((post) => (post.selected = false))
    this.updateSelectedPosts()
  }

  proxyImage(url: string): string {
    return this.PROXY_URL + encodeURIComponent(url)
  }

  closeDialog(): void {
    if (this.dialogRef) {
      this.dialogRef.close({ action: 'cancel' } as ScrapperDialogResult)
    } else {
      this.onClose.emit()
    }
  }

  cancelLoading(): void {
    this.loading = false
    this.error = ''
  }

  importImages(): void {
    if (this.selectedPosts.length === 0) return

    if (this.dialogRef) {
      this.dialogRef.close({
        action: 'import',
        images: this.selectedPosts
      } as ScrapperDialogResult)
    } else {
      this.onImport.emit(this.selectedPosts)
    }
  }

  // Reset the dialog state
  reset(): void {
    this.socialUrl = ''
    this.loading = false
    this.error = ''
    this.posts = []
    this.selectedPosts = []
    this.detectedPlatform = 'unknown'
  }
}
